import classNames from "classnames";
import React, { FC, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useGetTwoStepFirmQuery,
  useSetTwoStepFirmMutation,
} from "../../slices/TwoStepSlice";
import { twoStepErrorMessage } from "./twoStep.utils";
import { TwoStepModal } from "./TwoStepProfileCard";
import { ShieldIcon, tsSecondary, tsSmallPrimary } from "./TwoStepUi";

export const TWO_STEP_FIRM_TEXT =
  "Everyone in this firm must enter a 6-digit code from an authenticator app or their email after their password. People who haven't set it up yet are asked to do it the next time they use ProCharted.";

/**
 * Settings > Firm details: "Require two-step sign-in for everyone in this
 * firm". Admins and people with "View Settings" can change it (API checks too).
 */
export const TwoStepFirmCard: FC = () => {
  const orgId: string = getLinkedFirm()?.orgId || "";
  const { data, isLoading, isError } = useGetTwoStepFirmQuery(
    { orgId },
    { skip: !orgId },
  );
  const [save, saveState] = useSetTwoStepFirmMutation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = !!data?.available && !isError;
  const required = available && !!data?.required;
  const canManage = !!data?.canManage;

  const setRequired = async (value: boolean) => {
    setError(null);
    const result = await save({ orgId, required: value });
    if ("error" in result) setError(twoStepErrorMessage(result.error));
    setConfirming(false);
  };

  const toggle = () => {
    if (!canManage || saveState.isLoading) return;
    if (!required) setConfirming(true);
    else void setRequired(false);
  };

  return (
    <div
      className={
        "mt-6 flex items-start justify-between gap-6 rounded-xl border border-[#E4E9F2] bg-white p-6"
      }
      data-testid={"two-step-firm-card"}>
      <div className={"flex max-w-[640px] gap-3"}>
        <span
          className={
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F0EDFF] text-[#6B4EFF]"
          }>
          <ShieldIcon size={17} />
        </span>
        <div>
          <div className={"text-[15px] font-bold text-[#1F2433]"}>
            Require two-step sign-in for everyone in this firm
          </div>
          <div className={"mt-1 text-[13.5px] leading-[1.5] text-[#5B6678]"}>
            {TWO_STEP_FIRM_TEXT}
          </div>
          {isLoading ? null : !available ? (
            <div className={"mt-2 text-[13px] font-semibold text-[#8A90A2]"}>
              Two-step sign-in isn&apos;t available on this server yet.
            </div>
          ) : (
            <>
              {data && data.membersWithout > 0 && (
                <div className={"mt-2 text-[12.5px] text-[#8A90A2]"}>
                  {data.membersWithout === 1
                    ? "1 team member hasn't set it up yet."
                    : `${data.membersWithout} team members haven't set it up yet.`}
                </div>
              )}
              {data && data.membersWithout === 0 && (
                <div className={"mt-2 text-[12.5px] text-[#8A90A2]"}>
                  Everyone on the team has set it up.
                </div>
              )}
              {!canManage && (
                <div className={"mt-2 text-[12.5px] text-[#8A90A2]"}>
                  Only admins and people with Settings access can change this.
                </div>
              )}
            </>
          )}
          {error && (
            <div
              role={"alert"}
              className={"mt-2 text-[12.5px] font-semibold text-[#B4262C]"}>
              {error}
            </div>
          )}
        </div>
      </div>
      {available && (
        <label
          className={classNames(
            "flex shrink-0 items-center gap-2.5",
            canManage ? "cursor-pointer" : "cursor-not-allowed",
          )}>
          <span
            className={classNames(
              "text-[13px] font-semibold",
              required ? "text-[#5B3FE6]" : "text-[#8A90A2]",
            )}>
            {required ? "On" : "Off"}
          </span>
          <button
            type={"button"}
            role={"switch"}
            aria-checked={required}
            aria-label={"Require two-step sign-in for everyone in this firm"}
            disabled={!canManage || saveState.isLoading}
            onClick={toggle}
            className={classNames(
              "relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed",
              required ? "bg-[#6B4EFF]" : "bg-[#D5D9E2]",
              !canManage && "opacity-60",
              saveState.isLoading && "opacity-70",
            )}>
            <span
              className={classNames(
                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                required ? "left-[22px]" : "left-0.5",
              )}
            />
          </button>
        </label>
      )}
      {confirming && (
        <TwoStepModal
          title={"Require two-step sign-in?"}
          onClose={() => setConfirming(false)}>
          <span className={"text-[13.5px] leading-[1.6] text-[#3A4256]"}>
            Everyone in this firm, including you, will need a 6-digit code after
            their password. Anyone who hasn&apos;t set it up will be asked to
            set it up right away (about a minute: an authenticator app or email
            codes).
          </span>
          <div className={"flex justify-end gap-2.5"}>
            <button
              type={"button"}
              className={tsSecondary}
              onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              type={"button"}
              className={tsSmallPrimary}
              disabled={saveState.isLoading}
              onClick={() => void setRequired(true)}>
              {saveState.isLoading ? "Saving…" : "Require it"}
            </button>
          </div>
        </TwoStepModal>
      )}
    </div>
  );
};
